import React, { useEffect, useState } from "react";
import { Input } from "reactstrap";
import CmsFieldKey from "./CmsFieldKey";
import CmsFieldError from "./CmsFieldError";
import CmsImageDownloadButton from "./CmsImageDownloadButton";
import { resolveCmsAssetUrl } from "../utils/cmsAssetUrl";
import { fieldAnchorId } from "../utils/validationUi";

const INPUT_CLASS = "cms-input form-control";

function bilingualColProps(fieldKey, langCode, error) {
  if (!fieldKey) {
    return { className: "cms-bilingual-row__col" };
  }
  const errorKey = `${langCode}.${fieldKey}`;
  return {
    id: fieldAnchorId(errorKey),
    "data-cms-field": errorKey,
    className: error ? "cms-bilingual-row__col cms-bilingual-row__col--invalid" : "cms-bilingual-row__col",
  };
}

export function BilingualTableHeader() {
  return (
    <div className="cms-bilingual-head">
      <div className="cms-bilingual-head__label">
        <i className="mdi mdi-form-select" aria-hidden="true" />
        Field
      </div>
      <div className="cms-bilingual-head__col cms-bilingual-head__col--en">
        <span className="cms-lang-chip cms-lang-chip--en">EN</span>
        <span>English</span>
      </div>
      <div className="cms-bilingual-head__col cms-bilingual-head__col--es">
        <span className="cms-lang-chip cms-lang-chip--es">ES</span>
        <span>Español</span>
      </div>
    </div>
  );
}

export function BilingualTextRow({
  label,
  fieldKey,
  valueEn,
  valueEs,
  onChangeEn,
  onChangeEs,
  errorEn,
  errorEs,
  maxLength,
  type = "text",
  multiline = false,
  rows = 3,
  placeholderEn,
  placeholderEs,
}) {
  const inputProps = (value, onChange, error, lang, placeholder, langCode) => ({
    className: INPUT_CLASS,
    value: value || "",
    onChange: (e) => onChange(e.target.value),
    invalid: !!error,
    maxLength,
    type: multiline ? "textarea" : type,
    rows: multiline ? rows : undefined,
    placeholder: placeholder || `Enter ${label.toLowerCase()} (${lang})`,
    "aria-label": `${label} (${lang})`,
    "aria-invalid": !!error,
    "aria-describedby":
      error && fieldKey ? `${fieldAnchorId(`${langCode}.${fieldKey}`)}-error` : undefined,
  });

  return (
    <div className={`cms-bilingual-row${errorEn || errorEs ? " cms-bilingual-row--error" : ""}`}>
      <div className="cms-bilingual-row__label">
        <span className="cms-bilingual-row__title">{label}</span>
        {fieldKey && <CmsFieldKey fieldKey={fieldKey} />}
      </div>
      <div {...bilingualColProps(fieldKey, "en", errorEn)}>
        <Input {...inputProps(valueEn, onChangeEn, errorEn, "English", placeholderEn, "en")} />
        <CmsFieldError
          id={fieldKey ? `${fieldAnchorId(`en.${fieldKey}`)}-error` : undefined}
          message={errorEn}
        />
      </div>
      <div {...bilingualColProps(fieldKey, "es", errorEs)}>
        <Input {...inputProps(valueEs, onChangeEs, errorEs, "Spanish", placeholderEs, "es")} />
        <CmsFieldError
          id={fieldKey ? `${fieldAnchorId(`es.${fieldKey}`)}-error` : undefined}
          message={errorEs}
        />
      </div>
    </div>
  );
}

export function BilingualImageRow({
  label,
  fieldKey,
  srcEn,
  altEn,
  srcEs,
  altEs,
  onSrcEn,
  onAltEn,
  onSrcEs,
  onAltEs,
  onUploadEn,
  onUploadEs,
  srcErrorEn,
  altErrorEn,
  srcErrorEs,
  altErrorEs,
  uploadingEn,
  uploadingEs,
  showAlt = true,
}) {
  return (
    <div className="cms-bilingual-image-block">
      <div className="cms-bilingual-row__label cms-bilingual-image-block__title">
        <span className="cms-bilingual-row__title">
          <i className="mdi mdi-image-outline mr-1" aria-hidden="true" />
          {label}
        </span>
        {fieldKey && <CmsFieldKey fieldKey={fieldKey} />}
      </div>
      <div className="cms-bilingual-image-block__cols">
        <BilingualImageCell
          lang="EN"
          langCode="en"
          fieldAnchorBase={fieldKey}
          src={srcEn}
          alt={altEn}
          onSrc={onSrcEn}
          onAlt={onAltEn}
          onUpload={onUploadEn}
          srcError={srcErrorEn}
          altError={altErrorEn}
          uploading={uploadingEn}
          showAlt={showAlt}
        />
        <BilingualImageCell
          lang="ES"
          langCode="es"
          fieldAnchorBase={fieldKey}
          src={srcEs}
          alt={altEs}
          onSrc={onSrcEs}
          onAlt={onAltEs}
          onUpload={onUploadEs}
          srcError={srcErrorEs}
          altError={altErrorEs}
          uploading={uploadingEs}
          showAlt={showAlt}
        />
      </div>
    </div>
  );
}

function BilingualImageCell({
  lang,
  langCode,
  fieldAnchorBase,
  src,
  alt,
  onAlt,
  onUpload,
  srcError,
  altError,
  uploading,
  showAlt = true,
}) {
  const code = langCode || (lang === "EN" ? "en" : "es");
  const srcFieldKey = fieldAnchorBase
    ? showAlt
      ? `${code}.${fieldAnchorBase}.src`
      : `${code}.${fieldAnchorBase}`
    : null;
  const altFieldKey = fieldAnchorBase && showAlt ? `${code}.${fieldAnchorBase}.alt` : null;
  const [broken, setBroken] = useState(false);
  const previewSrc = resolveCmsAssetUrl(src);
  const storedPath = (src || "").trim();

  useEffect(() => {
    setBroken(false);
  }, [storedPath, previewSrc]);

  return (
    <div className={`cms-bilingual-image-cell cms-bilingual-image-cell--${lang.toLowerCase()}`}>
      <span className={`cms-lang-chip cms-lang-chip--${lang.toLowerCase()}`}>{lang}</span>
      <div className="cms-bilingual-image-cell__preview-wrap">
        {previewSrc && !broken ? (
          <>
            <CmsImageDownloadButton previewUrl={previewSrc} storedPath={storedPath} />
            <img
              key={previewSrc}
              src={previewSrc}
              alt={alt || "Preview"}
              className="cms-bilingual-image-cell__thumb"
              onError={() => setBroken(true)}
            />
          </>
        ) : (
          <div className="cms-bilingual-image-cell__thumb--empty" role="status">
            <i className="mdi mdi-image-off-outline" />
            <span>{storedPath ? "Preview unavailable" : "No image"}</span>
          </div>
        )}
      </div>
      <div
        className="cms-bilingual-image-cell__anchor"
        {...(srcFieldKey
          ? {
              id: fieldAnchorId(srcFieldKey),
              "data-cms-field": srcFieldKey,
            }
          : {})}
      >
        <label className="cms-file-upload-btn">
          <i className="mdi mdi-upload" />
          {uploading ? "Uploading…" : "Choose file"}
          <input
            type="file"
            accept="image/*"
            className="cms-file-upload-btn__input"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file && onUpload) onUpload(file);
              e.target.value = "";
            }}
          />
        </label>
        <CmsFieldError
          id={srcFieldKey ? `${fieldAnchorId(srcFieldKey)}-error` : undefined}
          message={srcError}
        />
      </div>
      {showAlt ? (
        <div
          className="cms-bilingual-image-cell__anchor"
          {...(altFieldKey
            ? {
                id: fieldAnchorId(altFieldKey),
                "data-cms-field": altFieldKey,
              }
            : {})}
        >
          <label className="cms-field-mini-label">Alt text (optional)</label>
          <Input
            className={INPUT_CLASS}
            value={alt || ""}
            placeholder="Describe the image"
            onChange={(e) => onAlt(e.target.value)}
            invalid={!!altError}
            aria-invalid={!!altError}
            aria-describedby={altError && altFieldKey ? `${fieldAnchorId(altFieldKey)}-error` : undefined}
          />
          <CmsFieldError
            id={altFieldKey ? `${fieldAnchorId(altFieldKey)}-error` : undefined}
            message={altError}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Image path fields (preview + upload), EN / ES columns. */
export function BilingualImagePathRow({
  label,
  fieldKey,
  srcEn,
  srcEs,
  onUploadEn,
  onUploadEs,
  srcErrorEn,
  srcErrorEs,
  uploadingEn,
  uploadingEs,
}) {
  return (
    <div className="cms-bilingual-image-block">
      <div className="cms-bilingual-row__label cms-bilingual-image-block__title">
        <span className="cms-bilingual-row__title">
          <i className="mdi mdi-image-outline mr-1" aria-hidden="true" />
          {label}
        </span>
        {fieldKey && <CmsFieldKey fieldKey={fieldKey} />}
      </div>
      <div className="cms-bilingual-image-block__cols">
        <BilingualImageCell
          lang="EN"
          langCode="en"
          fieldAnchorBase={fieldKey}
          src={srcEn}
          alt=""
          onAlt={() => {}}
          onUpload={onUploadEn}
          srcError={srcErrorEn}
          altError={null}
          uploading={uploadingEn}
          showAlt={false}
        />
        <BilingualImageCell
          lang="ES"
          langCode="es"
          fieldAnchorBase={fieldKey}
          src={srcEs}
          alt=""
          onAlt={() => {}}
          onUpload={onUploadEs}
          srcError={srcErrorEs}
          altError={null}
          uploading={uploadingEs}
          showAlt={false}
        />
      </div>
    </div>
  );
}

export function BilingualRepeaterRow({
  label,
  fieldKey,
  pointsEn,
  pointsEs,
  onChangeEn,
  onChangeEs,
  errorEn,
  errorEs,
  minItems = 1,
}) {
  const renderList = (points, onChange, error, lang) => {
    const list = Array.isArray(points) ? points : [];

    return (
    <div className="cms-bilingual-repeater">
      {list.map((item, index) => (
        <div key={`${lang}-${index}`} className="cms-bilingual-repeater__item">
          <Input
            className={INPUT_CLASS}
            type="textarea"
            rows={2}
            value={item || ""}
            placeholder={`Bullet ${index + 1}`}
            onChange={(e) => {
              const next = [...list];
              next[index] = e.target.value;
              onChange(next);
            }}
          />
          {list.length > minItems && (
            <button
              type="button"
              className="cms-bilingual-repeater__remove"
              onClick={() => onChange(list.filter((_, i) => i !== index))}
              aria-label="Remove bullet"
            >
              <i className="mdi mdi-close" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        className="cms-bilingual-repeater__add"
        onClick={() => onChange([...list, ""])}
      >
        <i className="mdi mdi-plus" /> Add bullet
      </button>
      <CmsFieldError message={error} />
    </div>
    );
  };

  return (
    <div className="cms-bilingual-block">
      <div className="cms-bilingual-row__label mb-2">
        <span className="cms-bilingual-row__title">{label}</span>
        {fieldKey && <CmsFieldKey fieldKey={fieldKey} />}
      </div>
      <div className="cms-bilingual-image-block__cols">
        <div
          className="cms-bilingual-image-cell cms-bilingual-image-cell--en"
          {...bilingualColProps(fieldKey, "en", errorEn)}
        >
          <span className="cms-lang-chip cms-lang-chip--en">EN</span>
          {renderList(pointsEn, onChangeEn, errorEn, "en")}
        </div>
        <div
          className="cms-bilingual-image-cell cms-bilingual-image-cell--es"
          {...bilingualColProps(fieldKey, "es", errorEs)}
        >
          <span className="cms-lang-chip cms-lang-chip--es">ES</span>
          {renderList(pointsEs, onChangeEs, errorEs, "es")}
        </div>
      </div>
    </div>
  );
}
